// Settings → UC CDR. The CDR feeds' credentials and the usage-alert thresholds
// live here with the rest of the platform configuration; the UC CDR app itself
// only runs them and reads the results.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2, Save, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Sources from "../uccdr/Sources";

const TZS = ["America/Edmonton", "America/Vancouver", "America/Toronto", "America/Halifax", "UTC"];
const NUMBERS = [
  ["multiple", "Flag at this multiple of its own baseline", "3"],
  ["window_days", "Window to judge (days)", "7"],
  ["baseline_shift_days", "Compare against this many days earlier", "28"],
  ["min_minutes", "Ignore domains under (minutes)", "60"],
  ["min_amount", "Ignore domains under (charges)", "25"],
];

export default function UCCDRSettings({ canEdit = true }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/uc-cdr/alert-settings");
    setCfg(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      const body = {
        enabled: !!cfg.enabled, multiple: Number(cfg.multiple) || 3,
        min_minutes: Number(cfg.min_minutes) || 0, min_amount: Number(cfg.min_amount) || 0,
        window_days: Number(cfg.window_days) || 7,
        baseline_shift_days: Number(cfg.baseline_shift_days) || 28,
        watch_minutes: !!cfg.watch_minutes, watch_spend: !!cfg.watch_spend,
        watch_drop: !!cfg.watch_drop, drop_pct: Number(cfg.drop_pct) || 80,
        frequency: cfg.frequency || "daily", tz: cfg.tz || TZS[0],
        recipients: String(cfg.recipients_text ?? (cfg.recipients || []).join(", "))
          .split(/[,;\s]+/).filter((r) => r.includes("@")),
      };
      const { data } = await api.put("/uc-cdr/alert-settings", body);
      setCfg(data);
      toast.success("Alert rules saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the alert rules");
    } finally {
      setBusy("");
    }
  };

  const testEmail = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/uc-cdr/alerts/test-email");
      toast[data.ok ? "success" : "warning"](
        data.ok ? `Test digest sent to ${data.sent_to.join(", ")}` : data.note || data.status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the test digest");
    } finally {
      setBusy("");
    }
  };

  const set = (key) => (e) => setCfg((c) => ({ ...c, [key]: e.target.value }));
  const toggle = (key) => (e) => setCfg((c) => ({ ...c, [key]: e.target.checked }));

  return (
    <div className="space-y-6" data-testid="settings-uc-cdr">
      <div>
        <h3 className="text-lg font-semibold text-slate-800">CDR sources</h3>
        <p className="mb-3 text-sm text-slate-500">
          One S3 source per SBC (bucket + prefix + key) and one per UC Platform environment.
          Secrets are encrypted at rest and never shown again — leave the field blank to keep
          the stored key. Use <strong>Test</strong> to prove a bucket before the first ingest;
          ingests and backfills run from UC CDR → Sources.
        </p>
        <Sources mode="manage" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-800">Usage alerts</h3>
        <p className="mb-3 max-w-3xl text-sm text-slate-500">
          Each domain is compared with <strong>itself</strong> — the last few days against the same
          stretch a month earlier — so a small account tripling its minutes is as visible as a big
          one. The floors stop tiny accounts crying wolf.
        </p>
        {!cfg ? (
          <Card className="px-4 py-6 text-sm text-slate-500">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </Card>
        ) : (
          <Card className="space-y-4 px-4 py-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700"
                   data-testid="uc-cdr-alerts-enabled">
              <input type="checkbox" className="h-4 w-4" checked={!!cfg.enabled}
                     onChange={toggle("enabled")} disabled={!canEdit} />
              Watch for unusual usage
            </label>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {NUMBERS.map(([key, label, ph]) => (
                <div key={key}>
                  <Label className="text-xs text-slate-500">{label}</Label>
                  <Input type="number" step="any" value={cfg[key] ?? ""} placeholder={ph}
                         disabled={!canEdit} data-testid={`uc-cdr-alerts-${key}`}
                         onChange={set(key)} />
                </div>
              ))}
              <div>
                <Label className="text-xs text-slate-500">Email digest</Label>
                <select value={cfg.frequency || "daily"} onChange={set("frequency")}
                        disabled={!canEdit} data-testid="uc-cdr-alerts-frequency"
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
                  <option value="daily">Daily (06:00)</option>
                  <option value="weekly">Weekly (Monday)</option>
                  <option value="off">Off — in-app only</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Clock used for the digest</Label>
                <select value={cfg.tz || TZS[0]} onChange={set("tz")} disabled={!canEdit}
                        data-testid="uc-cdr-alerts-tz"
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
                  {TZS.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500">Send to (comma separated)</Label>
                <Input value={cfg.recipients_text ?? (cfg.recipients || []).join(", ")}
                       placeholder="billing@unitedcloud.ca" disabled={!canEdit}
                       data-testid="uc-cdr-alerts-recipients"
                       onChange={(e) => setCfg((c) => ({ ...c, recipients_text: e.target.value }))} />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3">
              {[["watch_minutes", "Minutes"], ["watch_spend", "Charges"],
                ["watch_drop", "Also flag a collapse in traffic"]].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-slate-600"
                       data-testid={`uc-cdr-alerts-${key}`}>
                  <input type="checkbox" className="h-4 w-4" checked={!!cfg[key]}
                         onChange={toggle(key)} disabled={!canEdit} />
                  {label}
                </label>
              ))}
              {cfg.watch_drop && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">drop of at least</Label>
                  <Input type="number" className="h-8 w-20" value={cfg.drop_pct ?? 80}
                         disabled={!canEdit} data-testid="uc-cdr-alerts-drop_pct"
                         onChange={set("drop_pct")} />
                  <span className="text-xs text-slate-500">%</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              <Button size="sm" onClick={save} disabled={!canEdit || !!busy}
                      data-testid="uc-cdr-alerts-save">
                {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rules
              </Button>
              <Button size="sm" variant="outline" onClick={testEmail} disabled={!canEdit || !!busy}
                      data-testid="uc-cdr-alerts-test">
                {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send a test digest
              </Button>
              <span className="text-[12px] text-slate-500">
                {cfg.email_enabled ? "" : "Email is not configured — alerts stay in-app. "}
                {cfg.emails_paused ? "Outbound email is PAUSED platform-wide. " : ""}
                {cfg.last_run_at ? `Last check ${cfg.last_run_at.slice(0, 16).replace("T", " ")} UTC · ${cfg.last_run_found || 0} flagged.` : ""}
              </span>
              <a href="/uc-cdr" className="ml-auto" data-testid="uc-cdr-alerts-open-app">
                <Button size="sm" variant="ghost"><BellRing className="h-4 w-4" /> Open UC CDR → Alerts</Button>
              </a>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
